import { useCallback, useMemo } from 'react';
import { useQuery, useMutation } from '@ts-core/lib/apollo';

import { getErrorMessage, useMutationCompletionHandler, useListReorder } from '@ts-core/lib/apollo';

import { getMediaItemsBySubjectQuery } from '../graphql/queries';
import { reorderMediaItemMutation, toggleMediaItemMutation, deleteMediaItemMutation, importMediaItemsFromCourseMutation } from '../graphql/mutations';

export default function useMediaItems({ courseId, subjectId, onMediaItemsImportedFromCourse }) {
  const { onError, removeItemFromCache } = useMutationCompletionHandler();

  /**
   * Get media items
   */
  const {
    data,
    loading,
    error,
    refetch: refetchMediaItems,
  } = useQuery(getMediaItemsBySubjectQuery, {
    variables: { courseId, subjectId },
    skip: !courseId || !subjectId,
  });
  const mediaItems = useMemo(() => data?.mediaItems || [], [data]);

  /**
   * Delete Media Item
   */
  const [deleteMediaItem] = useMutation(deleteMediaItemMutation, {
    onError,
    update: removeItemFromCache({
      query: getMediaItemsBySubjectQuery,
      variables: { courseId, subjectId },
      queryField: 'mediaItems',
      mutationField: 'mediaItem',
    }),
  });
  const onDeleteMediaItem = useCallback(
    (mediaItem) => {
      deleteMediaItem({
        variables: { courseId, subjectId, mediaItemId: mediaItem._id },
        optimisticResponse: {
          mediaItem,
        },
      });
    },
    [deleteMediaItem, courseId, subjectId]
  );

  /**
   * Toggle Media Item
   */
  const [toggleMediaItem] = useMutation(toggleMediaItemMutation, { onError });
  const onToggleMediaItem = useCallback(
    (mediaItem) => {
      const updatedMediaItem = {
        ...mediaItem,
        enabled: !mediaItem.enabled,
      };
      toggleMediaItem({
        variables: {
          courseId,
          subjectId,
          mediaItemId: mediaItem._id,
          status: updatedMediaItem.enabled ? 'enable' : 'disable',
        },
        optimisticResponse: {
          mediaItem: updatedMediaItem,
        },
      });
    },
    [toggleMediaItem, courseId, subjectId]
  );

  /**
   * Reorder media item
   */
  const [reorderMediaItems] = useMutation(reorderMediaItemMutation, {
    onError,
  });
  const { onReorderItems: onReorderMediaItems } = useListReorder({
    query: getMediaItemsBySubjectQuery,
    queryVariables: { courseId, subjectId },
    queryName: 'mediaItems',
    onCallMutation: (from, to) => {
      reorderMediaItems({
        variables: { courseId, subjectId, input: { from, to } },
      });
    },
  });

  /**
   * Import media items from course
   */
  const [importMediaItemsFromCourse, { loading: importingMediaItemsFromCourse }] = useMutation(importMediaItemsFromCourseMutation, {
    onError,
    onCompleted: onMediaItemsImportedFromCourse,
    refetchQueries: refetchMediaItems,
  });
  const onImportMediaItemsFromCourse = useCallback(
    (input) => {
      importMediaItemsFromCourse({ variables: { courseId, subjectId, input } });
    },
    [importMediaItemsFromCourse, courseId, subjectId]
  );

  return {
    mediaItems,
    loading,
    error: getErrorMessage({ error }),
    refetchMediaItems,

    onDeleteMediaItem,

    onToggleMediaItem,

    onReorderMediaItems,

    onImportMediaItemsFromCourse,
    importingMediaItemsFromCourse,
  };
}
